import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { api } from '@/lib/api';
import { fmtPrice } from '@/lib/format';
import { TID } from '@/lib/testIds';
import { toast } from 'sonner';
import { Plus, X, Trash2, Pencil, Sparkles, Image as ImageIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { resolveImageUrl } from '@/lib/img';

const empty = {
  category_id: '',
  name: '',
  description: '',
  image_url: '',
  available: true,
  has_sizes: false,
  sizes: [],
  price: '',
  tags: ['halal'],
  sort_order: 0,
  // Homepage placement (Phase 11.1)
  show_as_featured_dish: false,
  show_in_taste_of_this_place: false,
  featured_description: '',
  taste_caption: '',
  featured_sort_order: 0,
};

export default function AdminMenuItemsPage() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    const [it, c] = await Promise.all([
      api.get('/menu/items'),
      api.get('/menu/categories', { params: { active_only: false } }),
    ]);
    setItems(it.data || []);
    setCats(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...empty, category_id: cats[0]?.id || '' });
  const startEdit = (it) => setEditing({
    ...empty,
    ...it,
    price: it.price ?? '',
    sizes: it.sizes || [],
    tags: it.tags || [],
    // Fold legacy `featured` into the new flag if the doc predates the migration.
    show_as_featured_dish: it.show_as_featured_dish ?? it.featured ?? false,
    show_in_taste_of_this_place: it.show_in_taste_of_this_place ?? false,
    featured_description: it.featured_description ?? '',
    taste_caption: it.taste_caption ?? '',
    featured_sort_order: it.featured_sort_order ?? it.featured_sort ?? 0,
  });

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try {
      await api.delete(`/admin/items/${it.id}`);
      toast.success('Item deleted');
      await load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const save = async () => {
    const payload = { ...editing };
    if (payload.has_sizes) {
      payload.price = null;
      payload.sizes = payload.sizes.map((s) => ({ name: s.name, price: parseFloat(s.price) || 0 }));
    } else {
      payload.sizes = [];
      payload.price = parseFloat(payload.price) || 0;
    }
    payload.sort_order = parseInt(payload.sort_order, 10) || 0;
    payload.featured_sort_order = parseInt(payload.featured_sort_order, 10) || 0;
    // Mirror legacy field for read-back compatibility (until deprecation)
    payload.featured = !!payload.show_as_featured_dish;
    payload.featured_sort = payload.featured_sort_order;
    try {
      if (editing.id) {
        await api.put(`/admin/items/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/admin/items', payload);
        toast.success('Item created');
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((i) => i.category_id === filter)),
    [items, filter],
  );

  // Human-readable per-category positions (#1, #2, …) computed from current order.
  const positions = useMemo(() => {
    const groups = {};
    // Sort by (sort_order asc, name asc) to mirror the backend ordering.
    const sorted = [...items].sort((a, b) => {
      const sa = a.sort_order ?? 0;
      const sb = b.sort_order ?? 0;
      if (sa !== sb) return sa - sb;
      return (a.name || '').localeCompare(b.name || '');
    });
    for (const it of sorted) {
      const key = it.category_id;
      if (!groups[key]) groups[key] = [];
      groups[key].push(it.id);
    }
    const map = {};
    for (const key of Object.keys(groups)) {
      groups[key].forEach((id, idx) => {
        map[id] = { position: idx + 1, total: groups[key].length };
      });
    }
    return map;
  }, [items]);

  const move = async (it, direction) => {
    // Optimistic: figure out the neighbour in the same category and swap.
    const sameCat = items
      .filter((x) => x.category_id === it.category_id)
      .sort((a, b) => {
        const sa = a.sort_order ?? 0;
        const sb = b.sort_order ?? 0;
        if (sa !== sb) return sa - sb;
        return (a.name || '').localeCompare(b.name || '');
      });
    const idx = sameCat.findIndex((x) => x.id === it.id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sameCat.length) return;
    try {
      await api.post(`/admin/items/${it.id}/move`, { direction });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    }
  };

  const homepageBadges = (it) => {
    const feat = it.show_as_featured_dish ?? it.featured;
    const taste = it.show_in_taste_of_this_place;
    if (!feat && !taste) return <span className="text-white/40 text-xs">—</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {feat && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] border border-[hsl(var(--sb-gold))]/40">
            <Sparkles size={9} /> Featured
          </span>
        )}
        {taste && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--sb-green))]/15 text-[hsl(var(--sb-green))] border border-[hsl(var(--sb-green))]/40">
            <ImageIcon size={9} /> Taste
          </span>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div data-testid={TID.admin.menuItemsPage}>
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display text-3xl text-white">MENU ITEMS</h1>
          <button
            onClick={startNew}
            data-testid="menu-item-new-button"
            className="h-10 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold inline-flex items-center gap-2"
          >
            <Plus size={16} />New item
          </button>
        </div>
        <p className="text-sm text-white/65 mb-5">
          Manage dishes shown on the public menu. Use the <span className="inline-flex items-center gap-1 text-white/80"><ArrowUp size={11} /><ArrowDown size={11} /></span> buttons to reorder items <span className="text-white/80">within each category</span> — the public menu reflects this order.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setFilter('all')}
            className={`h-9 px-4 rounded-full border text-sm ${filter === 'all' ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]' : 'bg-[hsl(var(--sb-surface))] text-white/85 border-[hsl(var(--sb-border))]'}`}
          >
            All ({items.length})
          </button>
          {cats.map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`h-9 px-4 rounded-full border text-sm ${filter === c.id ? 'bg-[hsl(var(--sb-gold))] text-black border-[hsl(var(--sb-gold))]' : 'bg-[hsl(var(--sb-surface))] text-white/85 border-[hsl(var(--sb-border))]'}`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="sb-card overflow-x-auto">
          <table data-testid={TID.admin.dataTable} className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-white/55 bg-black/30">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Item</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Price</th>
                <th className="text-left px-4 py-3">Available</th>
                <th className="text-left px-4 py-3">Homepage</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const cat = cats.find((c) => c.id === it.category_id);
                const priceLabel = it.has_sizes
                  ? it.sizes.map((s) => `${s.name} ${fmtPrice(s.price)}`).join(' • ')
                  : fmtPrice(it.price);
                const pos = positions[it.id] || { position: 1, total: 1 };
                const isFirst = pos.position === 1;
                const isLast = pos.position === pos.total;
                return (
                  <tr key={it.id} className="border-t border-[hsl(var(--sb-border))]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <span
                          className="text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold))] border border-[hsl(var(--sb-gold))]/40 font-semibold min-w-[2.25rem] text-center"
                          data-testid={`menu-item-position-${it.id}`}
                          title={`Position ${pos.position} of ${pos.total} in ${cat?.name || 'category'}`}
                        >
                          #{pos.position}
                        </span>
                        <button
                          onClick={() => move(it, 'up')}
                          disabled={isFirst}
                          title={isFirst ? 'Already at top of category' : `Move up (within ${cat?.name || 'category'})`}
                          data-testid={`menu-item-move-up-${it.id}`}
                          className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/85 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowUp size={13} />
                        </button>
                        <button
                          onClick={() => move(it, 'down')}
                          disabled={isLast}
                          title={isLast ? 'Already at bottom of category' : `Move down (within ${cat?.name || 'category'})`}
                          data-testid={`menu-item-move-down-${it.id}`}
                          className="h-7 w-7 grid place-items-center rounded hover:bg-white/5 text-white/85 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <ArrowDown size={13} />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded overflow-hidden bg-black shrink-0">
                          <img src={resolveImageUrl(it.image_url)} alt="" className="h-full w-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate">{it.name}</div>
                          <div className="text-[11px] text-white/55 max-w-xs truncate">{it.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/85">{cat?.name}</td>
                    <td className="px-4 py-3 text-white/85">{priceLabel}</td>
                    <td className="px-4 py-3">
                      {it.available
                        ? <span className="text-[hsl(var(--sb-green))]">Yes</span>
                        : <span className="text-[hsl(var(--sb-red))]">No</span>}
                    </td>
                    <td className="px-4 py-3">{homepageBadges(it)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => startEdit(it)}
                        data-testid={`menu-item-edit-${it.id}`}
                        className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-white/80 inline-flex"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(it)}
                        className="h-8 w-8 grid place-items-center rounded-md hover:bg-white/5 text-[hsl(var(--sb-red))] inline-flex ml-1"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-white/55">No items.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={() => setEditing(null)}>
          <div
            className="w-full max-w-lg bg-[hsl(var(--sb-surface))] h-full overflow-y-auto border-l border-[hsl(var(--sb-border))]"
            onClick={(e) => e.stopPropagation()}
            data-testid="menu-item-editor"
          >
            <div className="p-5 border-b border-[hsl(var(--sb-border))] flex items-center justify-between sticky top-0 bg-[hsl(var(--sb-surface))] z-10">
              <div className="font-display text-2xl text-white">{editing.id ? 'EDIT ITEM' : 'NEW ITEM'}</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 grid place-items-center text-white/70">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* BASIC DETAILS */}
              <SectionHeader>Basic details</SectionHeader>
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <Field label="Description" textarea value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} />

              {/* Compact image preview — fixed height, controlled by uploader itself */}
              <ImageUploader
                label="Image"
                value={editing.image_url}
                onChange={(v) => setEditing({ ...editing, image_url: v })}
                aspect="video"
              />

              <label className="block">
                <span className="text-[11px] uppercase tracking-widest text-white/55">Category</span>
                <select
                  value={editing.category_id}
                  onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                >
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>

              {/* PRICING */}
              <SectionHeader>Pricing</SectionHeader>
              <label className="inline-flex items-center gap-2 text-sm text-white/85">
                <input
                  type="checkbox"
                  checked={editing.has_sizes}
                  onChange={(e) => setEditing({ ...editing, has_sizes: e.target.checked })}
                /> Has size variants
              </label>
              {editing.has_sizes ? (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/55 mb-2">Sizes</div>
                  <div className="space-y-2">
                    {editing.sizes.map((s, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          value={s.name}
                          onChange={(e) => { const arr = [...editing.sizes]; arr[idx] = { ...arr[idx], name: e.target.value }; setEditing({ ...editing, sizes: arr }); }}
                          placeholder="Name"
                          className="flex-1 h-10 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                        />
                        <input
                          value={s.price}
                          type="number"
                          step="0.01"
                          onChange={(e) => { const arr = [...editing.sizes]; arr[idx] = { ...arr[idx], price: e.target.value }; setEditing({ ...editing, sizes: arr }); }}
                          placeholder="Price"
                          className="w-28 h-10 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
                        />
                        <button
                          onClick={() => setEditing({ ...editing, sizes: editing.sizes.filter((_, i) => i !== idx) })}
                          className="h-10 w-10 grid place-items-center rounded-md border border-[hsl(var(--sb-border))] text-[hsl(var(--sb-red))]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setEditing({ ...editing, sizes: [...editing.sizes, { name: '', price: 0 }] })}
                      className="text-sm text-[hsl(var(--sb-gold))]"
                    >
                      + Add size
                    </button>
                  </div>
                </div>
              ) : (
                <Field label="Price (£)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} />
              )}

              {/* AVAILABILITY */}
              <SectionHeader>Availability</SectionHeader>
              <label className="inline-flex items-center gap-2 text-sm text-white/85">
                <input
                  type="checkbox"
                  checked={editing.available}
                  onChange={(e) => setEditing({ ...editing, available: e.target.checked })}
                /> Available
              </label>

              {/* HOMEPAGE DISPLAY (Phase 11.1) */}
              <SectionHeader>Homepage display</SectionHeader>
              <p className="text-xs text-white/55 -mt-3">
                Choose independently whether this dish appears in the Featured Dishes carousel and/or in the &ldquo;A Taste of This Place&rdquo; section.
              </p>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-white/90">
                  <input
                    type="checkbox"
                    checked={!!editing.show_as_featured_dish}
                    onChange={(e) => setEditing({ ...editing, show_as_featured_dish: e.target.checked })}
                    data-testid="editor-featured-dish-checkbox"
                  /> Show as Featured Dish
                </label>
                {editing.show_as_featured_dish && (
                  <div className="pl-6 space-y-3">
                    <Field
                      label="Featured description"
                      textarea
                      value={editing.featured_description}
                      onChange={(v) => setEditing({ ...editing, featured_description: v })}
                      testid="editor-featured-description"
                      hint="Short marketing sentence used in the homepage carousel"
                    />
                    <Field
                      label="Featured sort order"
                      type="number"
                      value={editing.featured_sort_order}
                      onChange={(v) => setEditing({ ...editing, featured_sort_order: v })}
                      hint="Lower = earlier in the carousel"
                    />
                  </div>
                )}
                <label className="flex items-center gap-2 text-sm text-white/90">
                  <input
                    type="checkbox"
                    checked={!!editing.show_in_taste_of_this_place}
                    onChange={(e) => setEditing({ ...editing, show_in_taste_of_this_place: e.target.checked })}
                    data-testid="editor-taste-checkbox"
                  /> Show in A Taste of This Place
                </label>
                {editing.show_in_taste_of_this_place && (
                  <div className="pl-6 space-y-3">
                    <Field
                      label="Taste caption"
                      value={editing.taste_caption}
                      onChange={(v) => setEditing({ ...editing, taste_caption: v })}
                      testid="editor-taste-caption"
                      hint="Short caption shown under the image on the homepage"
                    />
                    <p className="text-[11px] text-white/55">Image is inherited from this Menu Item — change the image above to update it here.</p>
                  </div>
                )}
              </div>

              {/* ADDITIONAL */}
              <SectionHeader>Additional</SectionHeader>
              <Field
                label="Tags (comma-separated)"
                value={(editing.tags || []).join(', ')}
                onChange={(v) => setEditing({ ...editing, tags: v.split(',').map((s) => s.trim()).filter(Boolean) })}
              />
              <Field
                label="Menu sort order"
                type="number"
                value={editing.sort_order}
                onChange={(v) => setEditing({ ...editing, sort_order: v })}
                hint="Order on the public menu (separate from homepage ordering)"
              />

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-[hsl(var(--sb-surface))] pb-1">
                <button
                  onClick={save}
                  data-testid="menu-item-save-button"
                  className="flex-1 h-11 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold"
                >
                  {editing.id ? 'Save changes' : 'Create item'}
                </button>
                <button onClick={() => setEditing(null)} className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

const SectionHeader = ({ children }) => (
  <div className="pt-2">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--sb-gold))] font-semibold">{children}</div>
    <div className="mt-1 border-t border-[hsl(var(--sb-border))]" />
  </div>
);

const Field = ({ label, value, onChange, type = 'text', textarea, hint, testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    {textarea ? (
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        data-testid={testid}
        className="mt-1 w-full p-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
      />
    ) : (
      <input
        type={type}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white"
      />
    )}
    {hint && <span className="text-[10px] text-white/45 mt-1 block">{hint}</span>}
  </label>
);
